import LocationOffIcon from '@mui/icons-material/LocationOff'
import {
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Typography,
  Switch,
  Paper,
} from '@mui/material'
import I18n from 'i18n-js'
import { useSnackbar } from 'notistack'
import { useEffect } from 'react'
import { useParams } from 'react-router'

import useCurrentRetailerAccount from '@src/lib/useCurrentRetailerAccount'
import { useLocations } from '@src/lib/useLocations'

import GoogleMap from '../shared/GoogleMap'

const RetailerSettingsBrandSettingsLocations: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const { ensureCurrentRetailerAccount } = useCurrentRetailerAccount()
  const params = useParams<{ brandSlug: string }>()
  const currentRetailerId = ensureCurrentRetailerAccount?.retailers?.find(
    (x) => String(x.brand?.slug) === String(params.brandSlug),
  )?.id

  const { locations, getLocations, createLocation, deleteLocation } =
    useLocations(ensureCurrentRetailerAccount)
  useEffect(() => {
    getLocations()
  }, [getLocations])

  const retailerLocations =
    locations
      ?.map(
        (location) =>
          location?.children?.find(
            (loc) =>
              loc?.locationable_id === currentRetailerId &&
              loc?.parent_id === location.id,
          ),
      )
      .filter((x) => x) || []

  return locations && currentRetailerId && retailerLocations ? (
    <Paper>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="left">
                    {I18n.t('retailerSettings.brandSettings.locations.address')}
                  </TableCell>
                  <TableCell align="right">
                    {I18n.t(
                      'retailerSettings.brandSettings.locations.connected',
                    )}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locations.map((location) => (
                  <TableRow>
                    <TableCell align="left">
                      {location.addr1}
                      {', '}
                      {location.city}
                      {', '}
                      {location.state}
                    </TableCell>
                    <TableCell align="right">
                      <Switch
                        checked={retailerLocations?.some(
                          (retailerLocation) =>
                            retailerLocation?.parent_id === location.id,
                        )}
                        onChange={async (e, checked) => {
                          if (checked) {
                            try {
                              await createLocation(
                                currentRetailerId,
                                'retailers',
                                {
                                  addr1: location?.addr1 || '',
                                  addr2: location?.addr2 || '',
                                  city: location?.city || '',
                                  state: location?.state || '',
                                  country_code: location?.country_code || 'US',
                                  zip: location?.zip || '',
                                  phone: location?.phone,
                                  phone_extension: location?.phone_extension,
                                  latitude: location?.latitude,
                                  longitude: location?.longitude,
                                  locationable_id: currentRetailerId,
                                  locationable_type: 'Retailer',
                                  parent_id: location.id,
                                },
                              )
                              enqueueSnackbar(
                                I18n.t(
                                  'retailerSettings.brandSettings.locations.notifications.connectSuccess',
                                ),
                                {
                                  variant: 'success',
                                },
                              )
                            } catch (e) {
                              console.error(e)
                              enqueueSnackbar(
                                I18n.t(
                                  'retailerSettings.brandSettings.locations.notifications.connectError',
                                ),
                                {
                                  variant: 'error',
                                },
                              )
                            }
                          } else {
                            try {
                              const locationId = retailerLocations.find(
                                (el) => el?.parent_id === location.id,
                              )?.id
                              if (!locationId) {
                                throw new Error('Location not found')
                              }
                              await deleteLocation(locationId)
                              enqueueSnackbar(
                                I18n.t(
                                  'retailerSettings.brandSettings.locations.notifications.disconnectSuccess',
                                ),
                                { variant: 'success' },
                              )
                            } catch (e) {
                              console.error(e)
                              enqueueSnackbar(
                                I18n.t(
                                  'retailerSettings.brandSettings.locations.notifications.disconnectError',
                                ),
                                {
                                  variant: 'error',
                                },
                              )
                            }
                          }
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid item xs={12} md={6}>
          {retailerLocations.length ? (
            <GoogleMap
              style={{ height: '400px' }}
              locations={retailerLocations.map((location) => ({
                latitude: location?.latitude || '',
                longitude: location?.longitude || '',
              }))}
            />
          ) : (
            <Box
              p={6}
              display="flex"
              height="100%"
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
            >
              <LocationOffIcon color="disabled" fontSize="large" />
              <Typography color="textSecondary">
                {I18n.t('manageBrands.noLocations')}
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </Paper>
  ) : null
}

export default RetailerSettingsBrandSettingsLocations
